/**
 * specialtyMatcher — normalisation et matching des spécialités après la
 * refonte Phase F3 (04/02/2026).
 *
 * Contexte : le doc « Analyse catalogue flotte » normalise les spécialités
 * sur 8 axes (Messagerie/Colis, Frigo/Pharma, BTP/Matériaux/Engins TP,
 * ADR/Produits sensibles, Export UE, Vrac/Palettes, Transport animaux,
 * ZFE). Beaucoup de fichiers data référencent encore les anciens libellés
 * (Frigorifique, BTP, Vrac, etc.) — au lieu de tout réécrire et risquer
 * de casser des dizaines de tests, on canonise via cet utilitaire.
 *
 * Usage :
 *   import { canonicalSpec, matchSpec } from './specialtyMatcher';
 *   canonicalSpec('Frigorifique') === canonicalSpec('Frigo/Pharma') // true
 *   matchSpec('Vrac lourd', 'Vrac/Palettes') // true
 */

/**
 * Map of canonical key → array of aliases used in the codebase.
 * Centralised here so any new alias only needs to be added once.
 */
const SPECIALTY_GROUPS = [
  {
    key: 'messagerie',
    canonical: 'Messagerie/Colis',
    aliases: [
      'Messagerie', 'Messagerie/Colis', 'Messagerie / Colis',
      'Messagerie express', 'Colis', 'Express courier', 'Courier',
    ],
  },
  {
    key: 'frigo',
    canonical: 'Frigo/Pharma',
    aliases: [
      'Frigorifique', 'Frigo/Pharma', 'Frigo / Pharma',
      'GMS', 'Pharma', 'Refrigerated', 'Refrigerated / Pharma',
    ],
  },
  {
    key: 'btp',
    canonical: 'BTP/Matériaux/Engins TP',
    aliases: [
      'BTP', 'BTP/Matériaux/Engins TP', 'BTP / Matériaux',
      'BTP lourd', 'Chantier', 'Engins de chantier',
      'Construction', 'Construction / Materials / Equipment',
    ],
  },
  {
    key: 'adr',
    canonical: 'ADR/Produits sensibles',
    aliases: [
      'ADR', 'ADR/Produits sensibles', 'ADR / Produits sensibles',
      'Matières dangereuses', 'Citerne', 'Spécialisé',
      'ADR / Sensitive goods',
    ],
  },
  {
    key: 'export',
    canonical: 'Export UE',
    aliases: [
      'Export UE', 'Export EU', 'Longue distance', 'EU', 'International',
      'EU export', 'Long distance',
    ],
  },
  {
    key: 'vrac',
    canonical: 'Vrac/Palettes',
    aliases: [
      'Vrac', 'Vrac/Palettes', 'Vrac / Palettes', 'Vrac lourd',
      'Palettes', 'Distribution', 'National', 'Bulk / Pallets',
    ],
  },
  // 05/02/2026 — Q1 retour user prod : entrée « animaux » (bétaillère)
  // supprimée du catalogue.
  {
    key: 'zfe',
    canonical: 'ZFE',
    aliases: ['ZFE', 'Image verte', 'Low Emission Zone'],
  },
  // 05/02/2026 — Q1 retour user prod : convoi exceptionnel (transport
  // d'éoliennes, fusée, maison préfab). Spécialité unique du tracteur T5
  // MANTOR Heavyhaul XXL, requiert permis dédié + intérim ponctuel.
  {
    key: 'convoi-exceptionnel',
    canonical: 'Convoi exceptionnel',
    aliases: [
      'Convoi exceptionnel', 'Transport exceptionnel', 'Exceptional convoy',
      'Heavy haul', 'Oversize load',
    ],
  },
];

const ALIAS_INDEX = new Map();
SPECIALTY_GROUPS.forEach((g) => {
  g.aliases.forEach((a) => ALIAS_INDEX.set(a.toLowerCase(), g));
});

/**
 * Return the canonical (normalised, F3) label for any specialty string.
 * Unknown strings are returned as-is.
 */
export function canonicalSpec(name) {
  if (!name || typeof name !== 'string') return name;
  const group = ALIAS_INDEX.get(name.toLowerCase());
  return group ? group.canonical : name;
}

/**
 * True iff two specialty strings refer to the same canonical group.
 * Tolerant to legacy + new names.
 */
export function matchSpec(a, b) {
  if (!a || !b) return false;
  const ga = ALIAS_INDEX.get(a.toLowerCase());
  const gb = ALIAS_INDEX.get(b.toLowerCase());
  if (ga && gb) return ga.key === gb.key;
  return a.toLowerCase() === b.toLowerCase();
}

/**
 * Return true iff `specs[]` contains a specialty that matches `target`.
 * Used by contract/vehicle assignment logic.
 */
export function specsInclude(specs, target) {
  if (!Array.isArray(specs)) return false;
  return specs.some((s) => matchSpec(s, target));
}

export const SPECIALTY_CANONICAL_LIST = SPECIALTY_GROUPS.map((g) => g.canonical);
